import { classes } from 'html-classes';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InputPhone from '~/components/InputPhone/InputPhone';
import ApplySMSButton from '~/components/ModalAuth/ApplySMSButton/ApplySMSButton';
import { useGlobal } from '~/hooks/useGlobal';

import DokobitErrorHandler from '../DokobitErrorHandler';
import PolicyAgreement from '../PolicyAgreement';

import { PHONE_MAX_LENGTH } from './constants';
import { PhoneNumberFormProps } from './interfaces';
import styles from './styles.module.scss';
import {
  clearTimeoutData,
  formatTime,
  getRemainingTime,
  isTestPhoneNumber,
} from './utils';

export default function PhoneNumberForm({
  phoneVal,
  policyAgreement,
  policyError,
  isLoading,
  dokobitError,
  dokobitFailed,
  phoneError,
  onPhoneChange,
  onPhoneValidate,
  onPolicyChange,
  onSubmit,
  enableTimeout = false,
  onTimeoutTick,
  onTimeoutEnd,
  onTryAgainWithDokobit,
  onTrySmsOtp,
  onChangeNumber,
}: PhoneNumberFormProps) {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();

  const [timeoutThreshold, setTimeoutThreshold] = useState<number>(0);
  const timeoutRef = useRef<Nullable<ReturnType<typeof setInterval>>>(null);

  const handlePolicyChange = (checked: boolean) => {
    const valid = checked ? true : null;
    onPolicyChange(checked, valid);
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    if (timeoutThreshold > 0 && enableTimeout) {
      return;
    }

    onSubmit(e);
  };

  const renderActionButtons = () => {
    const button = (
      <ApplySMSButton
        isLoading={isLoading}
        isDisabled={timeoutThreshold > 0}
        className={classes(['_full-width', styles.actionBtn])}
      >
        {timeoutThreshold > 0 && !isLoading
          ? t('phrases:continueDisabled', {
              time: formatTime(timeoutThreshold, {
                secondsLabel: t('sec_other'),
                minutesLabel: t('min_other'),
              }),
            })
          : t('phrases:continue')}
      </ApplySMSButton>
    );

    if (isMobile || !dokobitFailed) {
      return button;
    }

    const isTestNumber = isTestPhoneNumber(phoneVal);

    return (
      <div className={styles.dokobitButtons}>
        <button
          type="button"
          className="button _bordered _med"
          onClick={onTryAgainWithDokobit}
          disabled={isLoading}
        >
          {t('phrases:tryAgain')}
        </button>
        {isTestNumber ? (
          <button
            type="button"
            className="button _primary _med"
            onClick={onTrySmsOtp}
            disabled={isLoading}
          >
            {t('phrases:continue')}
          </button>
        ) : (
          <button
            type="button"
            className="button _primary _med"
            onClick={onChangeNumber}
            disabled={isLoading}
          >
            {t('changeNumber')}
          </button>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (enableTimeout) {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTimeoutThreshold(0);

      const remainingTime = getRemainingTime();

      if (remainingTime <= 0) {
        clearTimeoutData();
        onTimeoutEnd?.();
        return;
      }

      setTimeoutThreshold(remainingTime);

      timeoutRef.current = setInterval(() => {
        setTimeoutThreshold((prev) => {
          const newTime = prev - 1000;
          if (newTime <= 0) {
            if (timeoutRef.current) {
              clearInterval(timeoutRef.current);
            }
            clearTimeoutData();
            onTimeoutEnd?.();
            return 0;
          }
          onTimeoutTick?.(newTime);
          return newTime;
        });
      }, 1000);
    }
  }, [enableTimeout, onTimeoutEnd]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
    },
    [],
  );

  return (
    <form onSubmit={handleFormSubmit} className={styles.form}>
      <div className={styles.phoneNumberForm}>
        <div className={styles.title}>{t('phrases:loginOrRegister')}</div>
        <div className={styles.subtitle}>{t('phrases:enterPhoneNumber')}</div>

        <InputPhone
          attributeName="auth-number"
          label=""
          className={styles.phoneInput}
          value={phoneVal}
          showValidationError={false}
          onChange={onPhoneChange}
          onValidate={onPhoneValidate}
          autoFocus={true}
          maxLength={PHONE_MAX_LENGTH}
        />

        {phoneError && <p className="error-label">{phoneError}</p>}

        <PolicyAgreement
          policyAgreement={policyAgreement}
          policyError={policyError}
          onChange={handlePolicyChange}
        />

        <DokobitErrorHandler dokobitError={dokobitError} />

        {renderActionButtons()}
      </div>
    </form>
  );
}
